import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminDroneStorageModal from './AdminDroneStorageModal';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import adminService, {
  type AdminBillingPmPreview,
  type FarmLocationCheckReport,
  type FarmLocationCheckRow,
  type UserLayerRow,
  type UserPatchBody,
} from '../services/adminService';
import { deleteFarmViaDigisakaApi } from '../config/digisakaApi';
import type { User, RoleComputePresetDTO } from '../types/auth';
import { catalogPrimaryEndpointForKey, catalogUiForKey } from '../config/analysisLayersCatalog';
import {
  ADMIN_ROLE_LAYER_UI_GROUP_LABEL,
  ADMIN_ROLE_LAYER_UI_GROUP_ORDER,
  adminRoleLayerUiGroupForKey,
  sortRoleLayerRowsForGroup,
  type AdminRoleLayerUiGroupId,
} from '../config/adminRoleLayerGroups';
import {
  MAIN_CUSTOMER_ROLES,
  PLANTATION_SUB_ROLE_OPTIONS,
  ROLE_DISPLAY_LABELS,
  SIGNUP_ROLE_OPTIONS,
  accountTypeLabel,
  coerceRoleValue,
} from '../config/audience';
import {
  buildAdminComputePlanPatch,
  deriveExtraWholeHoursFromUser,
  presetForRole,
  presetSummaryLabel,
  type AdminComputeMode,
} from '../config/adminComputePresets';

const ROLES: readonly string[] = ['admin', ...MAIN_CUSTOMER_ROLES];
const PRIMARY_ROLE_TABS: readonly { value: string; label: string }[] = SIGNUP_ROLE_OPTIONS;
const SECONDARY_ROLE_TABS: readonly { value: string; label: string }[] = [
  { value: 'admin', label: 'Admin' },
];

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

/** API stores budgets in ms; admin table edits minutes. */
const MS_PER_MINUTE = 60000;

function budgetMsToMinutesInput(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const min = ms / MS_PER_MINUTE;
  if (Math.abs(min - Math.round(min)) < 1e-9) return String(Math.round(min));
  return String(Math.round(min * 1000) / 1000);
}

function parseMinutesToBudgetMs(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = parseFloat(t.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * MS_PER_MINUTE);
}

/** Same style as the map sidebar guest timer (m:ss). */
function formatTimeLeftShort(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function groupUserLayersByAppUi(layers: UserLayerRow[]): {
  groupId: AdminRoleLayerUiGroupId;
  label: string;
  rows: UserLayerRow[];
}[] {
  const buckets = new Map<AdminRoleLayerUiGroupId, UserLayerRow[]>();
  for (const id of ADMIN_ROLE_LAYER_UI_GROUP_ORDER) {
    buckets.set(id, []);
  }
  for (const layer of layers) {
    const g = adminRoleLayerUiGroupForKey(layer.key);
    buckets.get(g)!.push(layer);
  }
  return ADMIN_ROLE_LAYER_UI_GROUP_ORDER.filter((id) => (buckets.get(id) ?? []).length > 0).map(
    (groupId) => ({
      groupId,
      label: ADMIN_ROLE_LAYER_UI_GROUP_LABEL[groupId],
      rows: sortRoleLayerRowsForGroup(groupId, buckets.get(groupId) ?? []),
    })
  );
}

function resolvedUserLayerUi(r: UserLayerRow): string {
  const fromDb = r.ui?.trim();
  if (fromDb) return fromDb;
  return catalogUiForKey(r.key) ?? '';
}

function resolvedUserLayerEndpoint(r: UserLayerRow): string {
  const fromDb = r.endpoint?.trim();
  if (fromDb) return fromDb;
  return catalogPrimaryEndpointForKey(r.key) ?? '';
}

function formatBillingPeriodEndUtc(iso?: string | null): string {
  const s = iso?.trim();
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return `${d.toLocaleString('en-US', {
      timeZone: 'UTC',
      dateStyle: 'medium',
      timeStyle: 'short',
    })} UTC`;
  } catch {
    return s;
  }
}

function computeModeMarketingShort(mode?: string | null): string {
  const m = (mode || 'daily').toLowerCase();
  if (m === 'monthly') return 'Monthly Sulit';
  if (m === 'unlimited') return 'Unlimited';
  return 'Daily';
}

function paidCadenceLabel(c?: string | null): string {
  const x = (c || '').trim().toLowerCase();
  if (x === 'annual') return 'Annual';
  if (x === 'monthly') return 'Monthly pay';
  return '—';
}

function formatCardExpAdmin(m?: number | null, y?: number | null): string | null {
  if (y == null && m == null) return null;
  let yy = y ?? 0;
  if (yy > 0 && yy < 100) yy += 2000;
  const mm = m != null ? String(m).padStart(2, '0') : '--';
  const yys = yy > 0 ? String(yy) : '--';
  return `${mm}/${yys}`;
}

function billingPreviewLoadError(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (e instanceof Error) return e.message;
  return 'Could not load card preview.';
}

/** PCI-safe snapshot we persist after PayMongo succeeds (shown in admin list). */
function formatStoredCardForAdmin(user: User): string | null {
  const rawBrand = user.payment_method_card_brand?.trim();
  const four = user.payment_method_last4?.trim();
  if (!rawBrand?.length && !four?.length) return null;
  const brandPretty = rawBrand?.length ? rawBrand.slice(0, 1).toUpperCase() + rawBrand.slice(1) : '';
  const mask = four?.length ? `········${four}` : '';
  if (brandPretty && mask) return `${brandPretty} ${mask}`;
  return brandPretty || mask || null;
}

function BillingAdminSnapshotCell({ u }: { u: User }) {
  if (u.is_system_guest) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const analysisLine = computeModeMarketingShort(u.compute_limit_mode);
  const cadence = paidCadenceLabel(u.subscription_billing_cadence ?? null);
  const autoRenewOn = u.billing_auto_renew !== false;
  const extraSnap = u.subscription_monthly_extra_hours;
  const showExtraSnap =
    (u.compute_limit_mode || '').toLowerCase() === 'monthly' && extraSnap != null && Number(extraSnap) > 0;

  const [pmBusy, setPmBusy] = useState(false);
  const [pmErr, setPmErr] = useState<string | null>(null);
  const [pmPrev, setPmPrev] = useState<AdminBillingPmPreview | null>(null);

  const loadPm = async () => {
    setPmBusy(true);
    setPmErr(null);
    try {
      const p = await adminService.getUserBillingPaymentMethodPreview(u.id);
      setPmPrev(p);
    } catch (e: unknown) {
      setPmErr(billingPreviewLoadError(e));
    } finally {
      setPmBusy(false);
    }
  };

  const expFmt = pmPrev?.fetched ? formatCardExpAdmin(pmPrev.exp_month, pmPrev.exp_year) : null;
  const storedCardLine = formatStoredCardForAdmin(u);
  const storedExpFmt = formatCardExpAdmin(u.payment_method_exp_month ?? null, u.payment_method_exp_year ?? null);

  return (
    <div className="max-w-[236px] space-y-1.5 text-[11px] leading-snug text-gray-800">
      <div>
        <span className="font-semibold text-gray-900">Subscribed analysis: </span>
        <span className="text-gray-700">{analysisLine}</span>
      </div>
      <div className="text-gray-700">
        <span className="text-gray-500">Billing cadence:</span> {cadence}{' '}
        <span className="text-gray-400">(checkout snapshot)</span>
      </div>
      <div className="text-gray-700">
        <span className="text-gray-500">Paid period ends:</span>{' '}
        <span className="whitespace-normal break-words">
          {formatBillingPeriodEndUtc(u.subscription_period_end ?? null)}
        </span>
      </div>
      <div className="text-gray-700">
        <span className="text-gray-500">Auto-renew:</span>{' '}
        <span className={autoRenewOn ? 'font-semibold text-emerald-800' : 'font-semibold text-amber-800'}>
          {autoRenewOn ? 'On' : 'Off'}
        </span>
      </div>
      {showExtraSnap ? (
        <div className="text-[10px] text-gray-600">
          Extra hours (snapshot): <span className="font-mono tabular-nums">{extraSnap}</span> h / mo
        </div>
      ) : null}
      <div className="border-t border-gray-100 pt-1.5 text-gray-700">
        <span className="text-gray-500">Card (stored): </span>
        {storedCardLine ? (
          <span className="font-mono text-[11px] font-semibold text-slate-900 tabular-nums">{storedCardLine}</span>
        ) : (
          <span className="text-gray-400 italic">— last 4 not saved yet</span>
        )}
        {storedExpFmt ? (
          <span className="block text-[10px] text-gray-500 mt-0.5">Exp (snapshot) {storedExpFmt}</span>
        ) : null}
        <span className="block text-[9px] text-gray-400 mt-0.5 leading-tight">
          Only brand + last 4 — full number is never stored.
        </span>
      </div>
      <button
        type="button"
        disabled={pmBusy}
        onClick={() => void loadPm()}
        className="text-left text-[11px] font-semibold text-violet-700 hover:underline disabled:cursor-wait disabled:opacity-60"
      >
        {pmBusy ? 'Loading…' : pmPrev ? 'Reload live check (PayMongo)' : 'Live check (PayMongo)'}
      </button>
      {pmErr ? (
        <p className="text-[10px] text-red-600" role="alert">
          {pmErr}
        </p>
      ) : null}
      {pmPrev ? (
        <div className="rounded-md border border-slate-200 bg-slate-50/90 px-2 py-1.5 text-[10px] text-slate-800 space-y-1">
          {!pmPrev.has_saved_payment_method ? (
            <p>No vaulted PayMongo method — user must pay again before auto-renew can run.</p>
          ) : pmPrev.fetched ? (
            <div className="space-y-1">
              <p className="font-semibold text-slate-900">{pmPrev.preview_label || 'Payment method'}</p>
              {expFmt ? <p className="text-slate-600 tabular-nums">Exp {expFmt}</p> : null}
              {pmPrev.paymongo_payment_method_hint ? (
                <p className="text-slate-500">
                  Vault ref{' '}
                  <code className="rounded bg-white px-1 py-px">{pmPrev.paymongo_payment_method_hint}</code>
                </p>
              ) : null}
              <p className="text-[9px] text-slate-500 leading-tight pt-0.5">
                Admin-only: brand & last digits. Full PAN and CVC are never visible.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {pmPrev.fetch_detail ? <p className="text-amber-900">{pmPrev.fetch_detail}</p> : null}
              {pmPrev.paymongo_payment_method_hint ? (
                <p className="text-slate-500">
                  Vault ref{' '}
                  <code className="rounded bg-white px-1 py-px text-[10px]">{pmPrev.paymongo_payment_method_hint}</code>
                </p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('blo');
  /** Empty = no `created_since` on the API (all registration dates). A date only shows users on/after that day. */
  const [since, setSince] = useState<string>('');

  const [computePresets, setComputePresets] = useState<RoleComputePresetDTO[]>([]);
  const [layerModalUserId, setLayerModalUserId] = useState<string | null>(null);
  const [layerRows, setLayerRows] = useState<UserLayerRow[]>([]);
  const [layerDraft, setLayerDraft] = useState<Record<string, boolean>>({});
  const [layerSaving, setLayerSaving] = useState(false);
  const [farmCheckUser, setFarmCheckUser] = useState<User | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('blo');
  const [inviteSubRole, setInviteSubRole] = useState('banana');
  const [inviteOrg, setInviteOrg] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');

  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminSuperuser, setAdminSuperuser] = useState(false);
  const [adminCreateBusy, setAdminCreateBusy] = useState(false);
  const [adminCreateMsg, setAdminCreateMsg] = useState('');
  const [legacyRoleTabs, setLegacyRoleTabs] = useState<{ value: string; label: string }[]>([]);

  const layerGroups = useMemo(() => groupUserLayersByAppUi(layerRows), [layerRows]);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const list = await adminService.listUsers({
        created_since: since.trim() || undefined,
        role: roleFilter,
        search: search.trim() || undefined,
        limit: 200,
      });
      setUsers(list);
      try {
        setComputePresets(await adminService.listRoleComputePresets());
      } catch {
        /* keep existing presets */
      }
      try {
        const leftover = await Promise.all(
          (['user', 'staff'] as const).map(async (role) => {
            const rows = await adminService.listUsers({ role, limit: 1 });
            return rows.length > 0 ? { value: role, label: ROLE_DISPLAY_LABELS[role] } : null;
          })
        );
        setLegacyRoleTabs(leftover.filter((x): x is { value: string; label: string } => Boolean(x)));
      } catch {
        /* ignore */
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e as Error).message ||
        'Failed to load users';
      setErr(String(msg));
    } finally {
      setLoading(false);
    }
  }, [since, roleFilter, search]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminUser(me)) return;
    void load();
  }, [authLoading, me, load]);

  useEffect(() => {
    if (authLoading) return;
    if (me && !isAdminUser(me)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, me, navigate]);

  const openLayerModal = async (userId: string) => {
    setLayerModalUserId(userId);
    setErr('');
    try {
      const rows = await adminService.getUserLayerEditor(userId);
      setLayerRows(rows);
      const d: Record<string, boolean> = {};
      for (const r of rows) {
        d[r.id] = r.effective_allow;
      }
      setLayerDraft(d);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to load layer editor';
      setErr(String(msg));
    }
  };

  const saveLayerOverrides = async () => {
    if (!layerModalUserId) return;
    setLayerSaving(true);
    setErr('');
    try {
      const assignments = layerRows.map((r) => ({
        layer_id: r.id,
        allow: layerDraft[r.id] ?? r.effective_allow,
      }));
      await adminService.putUserLayerOverrides(layerModalUserId, assignments);
      setLayerModalUserId(null);
      void load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save';
      setErr(String(msg));
    } finally {
      setLayerSaving(false);
    }
  };

  const resetLayerOverridesToRoleDefault = async () => {
    if (!layerModalUserId) return;
    if (
      !window.confirm(
        'Reset layer selection to the role default? This removes per-user overrides; enabled tools will match Admin → Layer tags for this user’s sign-up type (User/Staff use BLO).'
      )
    ) {
      return;
    }
    setLayerSaving(true);
    setErr('');
    try {
      await adminService.clearUserLayerOverrides(layerModalUserId);
      const rows = await adminService.getUserLayerEditor(layerModalUserId);
      setLayerRows(rows);
      const d: Record<string, boolean> = {};
      for (const r of rows) {
        d[r.id] = r.effective_allow;
      }
      setLayerDraft(d);
      void load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to reset layer access';
      setErr(String(msg));
    } finally {
      setLayerSaving(false);
    }
  };

  const patchUserRow = async (u: User, body: UserPatchBody) => {
    setErr('');
    try {
      const updated = await adminService.updateUser(u.id, body);
      setUsers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Update failed';
      setErr(String(msg));
    }
  };

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteMsg('');
    setErr('');
    const email = inviteEmail.trim();
    if (!email) {
      setInviteMsg('Enter an email address.');
      return;
    }
    setInviteBusy(true);
    try {
      const res = await adminService.inviteUser({
        email,
        role: inviteRole,
        sub_role: inviteRole === 'plantation_owner' ? inviteSubRole : undefined,
        organization: inviteOrg.trim() || undefined,
      });
      setInviteMsg(res.message);
      setInviteEmail('');
      setInviteOrg('');
    } catch (ex: unknown) {
      const msg =
        (ex as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to send invite';
      setInviteMsg(String(msg));
    } finally {
      setInviteBusy(false);
    }
  };

  const submitCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminCreateMsg('');
    setErr('');
    if (!adminEmail.trim() || !adminName.trim() || adminPassword.length < 8) {
      setAdminCreateMsg('Email, name, and password (8+ characters) are required.');
      return;
    }
    setAdminCreateBusy(true);
    try {
      const created = await adminService.createAdminUser({
        email: adminEmail.trim(),
        full_name: adminName.trim(),
        password: adminPassword,
        is_superuser: adminSuperuser,
      });
      setAdminCreateMsg(`Admin account created for ${created.email}. They can sign in immediately.`);
      setAdminEmail('');
      setAdminName('');
      setAdminPassword('');
      setAdminSuperuser(false);
      setRoleFilter('admin');
      void load();
    } catch (ex: unknown) {
      const msg =
        (ex as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to create admin';
      setAdminCreateMsg(String(msg));
    } finally {
      setAdminCreateBusy(false);
    }
  };

  const deleteUserRow = async (u: User) => {
    if (me && u.id === me.id) return;
    if (u.is_system_guest) return;
    if (
      !window.confirm(
        `Permanently delete ${u.email}? Related records may cascade. This cannot be undone.`,
      )
    ) {
      return;
    }
    setErr('');
    try {
      await adminService.deleteUser(u.id);
      setUsers((prev) => prev.filter((row) => row.id !== u.id));
      setLayerModalUserId((openId) => (openId === u.id ? null : openId));
    } catch (e: unknown) {
      const res = (e as { response?: { data?: { detail?: unknown }; status?: number } })?.response;
      const detail = res?.data?.detail;
      let msg = 'Delete failed';
      if (typeof detail === 'string') msg = detail;
      else if (res?.status === 500) {
        msg =
          'Delete failed on the server (often because this user still has farm fields or other linked data).';
      } else if (e instanceof Error && e.message) msg = e.message;
      setErr(msg);
    }
  };

  const headerNote = useMemo(
    () => (
      <span>
        Signed-in accounts use analysis rules from{' '}
        <Link to="/admin/role-compute-presets" className="text-emerald-700 font-medium hover:underline">
          Role analysis rules
        </Link>
        : daily and monthly snap to preset hours; for <strong>monthly</strong>, extra whole hours are capped per role in
        Role analysis rules. <strong>Unlimited</strong> means no monthly analysis cap. Guest row is only the per-IP
        try-out pool in minutes — see Tool usage for logs.
      </span>
    ),
    []
  );

  const roleFilterLabel = useMemo(() => {
    const all = [...PRIMARY_ROLE_TABS, ...SECONDARY_ROLE_TABS, ...legacyRoleTabs];
    return all.find((o) => o.value === roleFilter)?.label ?? roleFilter;
  }, [roleFilter, legacyRoleTabs]);

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Users & accounts">
        <div className="text-sm text-gray-600 mb-4 max-w-2xl leading-snug">{headerNote}</div>
        {err && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-sm">
            {err}
          </div>
        )}

        <div className="bg-white rounded-xl shadow border border-gray-200 p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Add accounts</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            <form onSubmit={submitInvite} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Invite user by email</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Sends a link so they can set their password. No verification code required.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Account type</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                >
                  {SIGNUP_ROLE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              {inviteRole === 'plantation_owner' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600">Crop</label>
                  <select
                    value={inviteSubRole}
                    onChange={(e) => setInviteSubRole(e.target.value)}
                    className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                  >
                    {PLANTATION_SUB_ROLE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div>
                <label className="block text-xs font-medium text-gray-600">Organization (optional)</label>
                <input
                  type="text"
                  value={inviteOrg}
                  onChange={(e) => setInviteOrg(e.target.value)}
                  className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={inviteBusy}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
              >
                {inviteBusy ? 'Sending…' : 'Send invite'}
              </button>
              {inviteMsg ? (
                <p className={`text-xs ${inviteMsg.includes('sent') ? 'text-emerald-800' : 'text-amber-900'}`}>
                  {inviteMsg}
                </p>
              ) : null}
            </form>

            <form onSubmit={submitCreateAdmin} className="rounded-lg border border-violet-100 bg-violet-50/40 p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Create admin directly</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Active immediately — no invitation or verification email.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Email</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Full name</label>
                <input
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  required
                  className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Password</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                  minLength={8}
                  className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={adminSuperuser}
                  onChange={(e) => setAdminSuperuser(e.target.checked)}
                />
                Superuser (full system access)
              </label>
              <button
                type="submit"
                disabled={adminCreateBusy}
                className="px-4 py-2 bg-violet-700 text-white rounded-lg text-sm font-medium hover:bg-violet-800 disabled:opacity-60"
              >
                {adminCreateBusy ? 'Creating…' : 'Create admin'}
              </button>
              {adminCreateMsg ? (
                <p
                  className={`text-xs ${
                    adminCreateMsg.includes('created') ? 'text-emerald-800' : 'text-amber-900'
                  }`}
                >
                  {adminCreateMsg}
                </p>
              ) : null}
            </form>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 p-4 mb-4 space-y-4">
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1.5">Filter by role</span>
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Show users for one role">
              {PRIMARY_ROLE_TABS.map((tab) => {
                const active = roleFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setRoleFilter(tab.value)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                      active
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <details className="mt-2 group">
              <summary className="cursor-pointer list-none text-xs text-gray-500 hover:text-gray-700 select-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1">
                <i className="ri-arrow-down-s-line transition-transform group-open:rotate-180" aria-hidden />
                Admin &amp; legacy roles
              </summary>
              <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100">
                {[...SECONDARY_ROLE_TABS, ...legacyRoleTabs].map((tab) => {
                  const active = roleFilter === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setRoleFilter(tab.value)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                        active
                          ? 'border-emerald-600 text-emerald-900 bg-emerald-50'
                          : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </details>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600">Registered on or after</label>
              <input
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
                className="mt-1 border rounded-md px-2 py-1.5 text-sm"
              />
              <p className="text-[11px] text-gray-500 mt-0.5 max-w-[220px]">
                Empty = all dates. Set to show signups on or after that day.
              </p>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600">Email search</label>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Contains…"
                className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => void load()}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 shrink-0"
            >
              Apply
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <div>
            <p className="text-xs text-gray-600 mb-2">
              <span className="font-medium text-gray-800">{users.length}</span> {roleFilterLabel}{' '}
              account{users.length === 1 ? '' : 's'} in this view.
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="p-2 font-medium min-w-[180px]" title="Email and role for this account">
                    Account
                  </th>
                  <th
                    className="p-2 font-medium min-w-[148px]"
                    title="Deactivate to block login and API access immediately (middleware + login both reject inactive users)."
                  >
                    Login / access
                  </th>
                  <th
                    className="p-2 font-medium min-w-[260px]"
                    title="Daily, Monthly, or Unlimited — aligned with Role analysis rules. Guest: IP pool minutes only."
                  >
                    Analysis plan
                  </th>
                  <th
                    className="p-2 font-medium min-w-[230px]"
                    title="Paid snapshot, auto-renew, stored last-4 + optional live PayMongo check — admins only."
                  >
                    Billing & renew
                  </th>
                  <th className="p-2 font-medium">Time left</th>
                  <th className="p-2 font-medium">Used (tool time)</th>
                  <th className="p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserTableRow
                    key={u.id}
                    u={u}
                    computePresets={computePresets}
                    currentAdminId={me?.id ?? null}
                    onPatch={(body) => void patchUserRow(u, body)}
                    onOpenLayers={() => void openLayerModal(u.id)}
                    onCheckFarms={() => setFarmCheckUser(u)}
                    onDelete={() => void deleteUserRow(u)}
                  />
                ))}
              </tbody>
            </table>
            {users.length === 0 && <p className="p-4 text-gray-500">No users match.</p>}
          </div>
          </div>
        )}

      {farmCheckUser && (
        <FarmLocationCheckModal
          user={farmCheckUser}
          onClose={() => setFarmCheckUser(null)}
        />
      )}

      {layerModalUserId && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center gap-2">
              <h2 className="text-lg font-semibold">Layer access</h2>
              <button
                type="button"
                onClick={() => setLayerModalUserId(null)}
                className="text-gray-500 hover:text-gray-800 shrink-0"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-3 overflow-y-auto flex-1 space-y-3">
              {layerGroups.map((g) => (
                <div key={g.groupId} className="rounded-lg border border-gray-200 bg-gray-50/80 overflow-hidden">
                  <div className="px-2.5 py-1.5 bg-white border-b border-gray-100">
                    <h3 className="text-xs font-semibold text-gray-900">{g.label}</h3>
                  </div>
                  <div className="divide-y divide-gray-100 bg-white">
                    {g.rows.map((r) => {
                      const uiLine = resolvedUserLayerUi(r);
                      const apiLine = resolvedUserLayerEndpoint(r);
                      return (
                        <label
                          key={r.id}
                          className="flex items-start gap-2 p-2 hover:bg-gray-50 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={layerDraft[r.id] ?? r.effective_allow}
                            onChange={(e) =>
                              setLayerDraft((d) => ({ ...d, [r.id]: e.target.checked }))
                            }
                            className="mt-0.5 shrink-0"
                          />
                          <span className="min-w-0">
                            <span className="font-medium">{r.name}</span>
                            <span className="text-gray-500"> ({r.key})</span>
                            {r.in_role_default && (
                              <span className="ml-1 text-xs text-emerald-700">from role</span>
                            )}
                            {uiLine ? (
                              <span className="block text-[11px] text-gray-600 mt-0.5">
                                <span className="text-gray-500">Where: </span>
                                {uiLine}
                              </span>
                            ) : null}
                            {r.description && (
                              <span className="block text-xs text-gray-600 mt-0.5 leading-snug">
                                {r.description}
                              </span>
                            )}
                            <span className="block text-[11px] mt-1">
                              <span className="text-gray-500">API: </span>
                              {apiLine ? (
                                <code className="text-violet-900 bg-violet-50 rounded px-1 break-all">
                                  {apiLine}
                                </code>
                              ) : (
                                <span className="text-gray-400 italic">not set</span>
                              )}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t flex flex-wrap gap-2 justify-between items-center">
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  type="button"
                  disabled={layerSaving || layerRows.length === 0}
                  onClick={() => void resetLayerOverridesToRoleDefault()}
                  className="text-xs px-2.5 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                >
                  Reset layers selection
                </button>
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center justify-center w-7 h-7 rounded-full border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                  title="Clears per-user layer overrides. Checkboxes will match audience tags for this user's role—the same tools enabled under Admin → Layer tags. User and Staff accounts follow BLO."
                  aria-label="Information: reset layers selection"
                >
                  <i className="ri-information-line text-base leading-none" aria-hidden />
                </button>
              </div>
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={() => setLayerModalUserId(null)}
                  className="px-3 py-1.5 rounded border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={layerSaving}
                  onClick={() => void saveLayerOverrides()}
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-50"
                >
                  {layerSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function ComputeRemainingCell({ u }: { u: User }) {
  if (u.is_system_guest) {
    return (
      <span
        className="text-violet-800"
        title="Shared guest account: remaining time is per client IP (same as a guest map session), not a single value here."
      >
        Per IP
      </span>
    );
  }
  if (u.compute_remaining_ms == null) {
    return (
      <span className="text-gray-500" title="No monthly/daily cap for this account (e.g. open unlimited).">
        No cap
      </span>
    );
  }
  return <span className="font-mono text-emerald-800">{formatTimeLeftShort(u.compute_remaining_ms)}</span>;
}

function PlanSegmented({
  value,
  onChange,
  disabled,
}: {
  value: AdminComputeMode;
  onChange: (m: AdminComputeMode) => void;
  disabled?: boolean;
}) {
  const opts: { id: AdminComputeMode; label: string }[] = [
    { id: 'daily', label: 'Daily' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'unlimited', label: 'Unlimited' },
  ];
  return (
    <div
      role="group"
      aria-label="Analysis plan"
      className={`inline-flex rounded-lg border border-gray-200 bg-gray-100/90 p-0.5 ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.id)}
          className={`px-2 py-1 text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap ${
            value === o.id
              ? 'bg-white text-emerald-900 shadow-sm ring-1 ring-gray-200/80'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ComputeUsedCell({ u }: { u: User }) {
  if (u.is_system_guest) {
    return (
      <span
        className="text-gray-500 text-sm max-w-[180px] inline-block"
        title="Guest usage is tracked per client IP, not on this user row. See usage log / auth/me guest_quota."
      >
        Per IP (not user row)
      </span>
    );
  }
  const t = u.compute_used_ms_today ?? 0;
  const mo = u.compute_used_ms_month ?? 0;
  return (
    <div className="leading-tight max-w-[140px]">
      <div>
        <span className="text-gray-400">Day </span>
        {formatTimeLeftShort(t)}
      </div>
      <div>
        <span className="text-gray-400">Month </span>
        {formatTimeLeftShort(mo)}
      </div>
    </div>
  );
}

function UserTableRow({
  u,
  computePresets,
  currentAdminId,
  onPatch,
  onOpenLayers,
  onCheckFarms,
  onDelete,
}: {
  u: User;
  computePresets: RoleComputePresetDTO[];
  currentAdminId: string | null;
  onPatch: (b: UserPatchBody) => void;
  onOpenLayers: () => void;
  onCheckFarms: () => void;
  onDelete: () => void;
}) {
  const [planMode, setPlanMode] = useState<AdminComputeMode>(() => {
    const m = (u.compute_limit_mode || 'daily').toLowerCase();
    if (m === 'monthly') return 'monthly';
    if (m === 'unlimited') return 'unlimited';
    return 'daily';
  });
  const [extraHours, setExtraHours] = useState(() => deriveExtraWholeHoursFromUser(u, computePresets));
  const [guestPoolMin, setGuestPoolMin] = useState(() => budgetMsToMinutesInput(u.compute_budget_ms_per_day));
  const [droneOpen, setDroneOpen] = useState(false);

  useEffect(() => {
    const m = (u.compute_limit_mode || 'daily').toLowerCase();
    if (m === 'monthly') setPlanMode('monthly');
    else if (m === 'unlimited') setPlanMode('unlimited');
    else setPlanMode('daily');
    setExtraHours(deriveExtraWholeHoursFromUser(u, computePresets));
    setGuestPoolMin(budgetMsToMinutesInput(u.compute_budget_ms_per_day));
  }, [u.id, u.compute_limit_mode, u.compute_budget_ms_per_month, u.compute_budget_ms_per_day, computePresets]);

  const pr = presetForRole(computePresets, u.role);
  const maxMoEx = pr.max_monthly_extra_hours;

  const flushSignedInPlan = (next?: { mode?: AdminComputeMode; extra?: number }) => {
    const mode = next?.mode ?? planMode;
    const ex = next?.extra ?? extraHours;
    onPatch(buildAdminComputePlanPatch(u.role, mode, ex, computePresets));
  };

  return (
    <>
    <tr className="border-t border-gray-100">
      <td className="p-2 align-top max-w-[220px]">
        <div className="break-all text-gray-900">{u.email}</div>
        {u.is_system_guest ? (
          <p className="mt-1 text-[11px] text-amber-800 font-medium">System guest (shared)</p>
        ) : (
          <div className="mt-1.5">
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Change role</label>
            <select
              value={coerceRoleValue(u.role)}
              onChange={(e) => {
                const r = e.target.value;
                onPatch({
                  role: r,
                  sub_role: r === 'plantation_owner' ? u.sub_role || 'banana' : null,
                  ...buildAdminComputePlanPatch(r, planMode, extraHours, computePresets),
                });
              }}
              className="border rounded px-1.5 py-1 text-xs w-full max-w-[200px] bg-white"
              title="Saves immediately. User leaves this tab if role no longer matches the filter."
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_DISPLAY_LABELS[r] || r}
                </option>
              ))}
            </select>
            {coerceRoleValue(u.role) === 'plantation_owner' ? (
              <select
                value={u.sub_role === 'coconut' ? 'coconut' : 'banana'}
                onChange={(e) => onPatch({ sub_role: e.target.value })}
                className="mt-1 border rounded px-1.5 py-1 text-xs w-full max-w-[200px] bg-white"
              >
                {PLANTATION_SUB_ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <p className="mt-1 text-[10px] text-gray-500">{accountTypeLabel(u.role, u.sub_role)}</p>
            )}
          </div>
        )}
      </td>
      <td className="p-2 align-top">
        {u.is_system_guest ? (
          <span
            className="text-[11px] text-gray-500 leading-snug"
            title="The shared guest account cannot be deactivated here."
          >
            Guest pool — use role rules &amp; IP quota only
          </span>
        ) : (
          <div className="max-w-[168px] space-y-1">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={u.is_active}
                onChange={(e) => onPatch({ is_active: e.target.checked })}
                className="mt-1 accent-emerald-600 shrink-0"
                title="Unchecked = deactivated: user cannot log in or call protected APIs."
              />
              <span className="text-[11px] leading-snug text-gray-900">
                <span className="font-semibold block">
                  {u.is_active ? 'Login allowed' : 'Deactivated'}
                </span>
                <span className="block text-[10px] text-gray-500 mt-0.5">
                  {u.is_active
                    ? 'Clear to block account (policy, region restriction, fraud, …).'
                    : 'Blocked from sign-in and API until re-enabled.'}
                </span>
              </span>
            </label>
          </div>
        )}
      </td>
      <td className="p-2 align-top">
        {u.is_system_guest ? (
          <div className="space-y-1 max-w-[220px]">
            <p className="text-[11px] text-amber-900/90 font-medium">Guest (IP pool)</p>
            <p className="text-[11px] text-gray-600 leading-snug">
              Not a signed-in plan. Set shared minutes per visitor IP below.
            </p>
            <label className="block text-[10px] font-medium text-gray-600">Guest pool (min / IP)</label>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              title="Total tool-time minutes per visitor IP for the shared guest account."
              className="w-24 border rounded px-1 py-0.5 text-xs border-amber-200 bg-amber-50/40"
              value={guestPoolMin}
              onChange={(e) => setGuestPoolMin(e.target.value)}
              onBlur={() =>
                onPatch({
                  compute_budget_ms_per_day: parseMinutesToBudgetMs(guestPoolMin),
                })
              }
            />
          </div>
        ) : (
          <div className="space-y-2 max-w-[280px]">
            <PlanSegmented
              value={planMode}
              onChange={(m) => {
                setPlanMode(m);
                onPatch(buildAdminComputePlanPatch(u.role, m, extraHours, computePresets));
              }}
            />
            <p className="text-[11px] text-gray-800 leading-snug border-l-2 border-emerald-200 pl-2">
              {presetSummaryLabel(u.role, planMode, computePresets)}
            </p>
            {planMode === 'monthly' && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <label htmlFor={`extra-${u.id}`} className="text-[11px] text-gray-600 whitespace-nowrap">
                  Extra hours
                </label>
                <input
                  id={`extra-${u.id}`}
                  type="number"
                  min={0}
                  max={maxMoEx}
                  step={1}
                  className="w-16 border rounded px-1 py-0.5 text-xs tabular-nums"
                  value={extraHours}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (Number.isNaN(v)) setExtraHours(0);
                    else setExtraHours(Math.max(0, Math.min(maxMoEx, v)));
                  }}
                  onBlur={() => flushSignedInPlan()}
                />
                <span className="text-[10px] text-gray-500">max {maxMoEx}</span>
              </div>
            )}
          </div>
        )}
      </td>
      <td className="p-2 align-top">
        <BillingAdminSnapshotCell u={u} />
      </td>
      <td className="p-2 align-top text-xs text-gray-800 whitespace-nowrap">
        <ComputeRemainingCell u={u} />
      </td>
      <td className="p-2 align-top text-xs text-gray-600">
        <ComputeUsedCell u={u} />
      </td>
      <td className="p-2 align-top">
        {u.is_system_guest ? (
          <span className="text-xs text-gray-400">—</span>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <button
              type="button"
              onClick={onOpenLayers}
              className="text-emerald-700 text-xs font-medium hover:underline text-left"
            >
              Layers
            </button>
            <button
              type="button"
              onClick={() => setDroneOpen(true)}
              className="text-emerald-700 text-xs font-medium hover:underline text-left"
            >
              Drone storage
            </button>
            {coerceRoleValue(u.role) === 'lgu' && u.external_user_id ? (
              <button
                type="button"
                onClick={onCheckFarms}
                className="text-sky-700 text-xs font-medium hover:underline text-left"
                title="Compare farm centroids to this LGU monitoring area via Google Earth Engine"
              >
                Check farms
              </button>
            ) : null}
            {currentAdminId && u.id !== currentAdminId ? (
              <button
                type="button"
                onClick={onDelete}
                className="text-red-700 text-xs font-medium hover:underline text-left"
                title="Permanently remove this user row (cannot delete yourself here)."
              >
                Delete…
              </button>
            ) : null}
          </div>
        )}
      </td>
    </tr>
    {droneOpen ? createPortal(<AdminDroneStorageModal user={u} onClose={() => setDroneOpen(false)} />, document.body) : null}
    </>
  );
}

function monitoringAreaLabel(area: Record<string, unknown> | null | undefined): string {
  if (!area || typeof area !== 'object') return '—';
  const level = String(area.level || '').trim();
  const name =
    (area.barangay as string) ||
    (area.municipality as string) ||
    (area.province as string) ||
    (area.city_id as string) ||
    (area.province_id as string) ||
    '';
  return [level, name].filter(Boolean).join(' · ') || '—';
}

function FarmLocationCheckModal({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [report, setReport] = useState<FarmLocationCheckReport | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');
  const [needsToken, setNeedsToken] = useState(false);

  const runCheck = async (accessToken?: string) => {
    setLoading(true);
    setErr('');
    setReport(null);
    setSelected({});
    setDeleteMsg('');
    try {
      const data = await adminService.checkUserFarmLocations(user.id, {
        digisakaAccessToken: accessToken?.trim() || undefined,
        vaultToken: true,
      });
      setReport(data);
      setNeedsToken(false);
      const init: Record<string, boolean> = {};
      for (const row of data.incorrect || []) {
        if (row.farm_id != null) init[String(row.farm_id)] = false;
      }
      setSelected(init);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e as Error).message ||
        'Farm location check failed';
      const text = String(msg);
      setErr(text);
      if (
        status === 401 ||
        /missing digisaka\.app session|session expired|log in again/i.test(text)
      ) {
        setNeedsToken(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per user open
  }, [user.id]);

  const incorrect: FarmLocationCheckRow[] = report?.incorrect || [];
  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([id]) => id);

  const toggleAll = (on: boolean) => {
    const next: Record<string, boolean> = {};
    for (const row of incorrect) {
      if (row.farm_id != null) next[String(row.farm_id)] = on;
    }
    setSelected(next);
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedIds.length} farm(s)? This cannot be undone. To fix location instead, correct it upstream in digisaka and re-create the farm.`
      )
    ) {
      return;
    }
    setDeleting(true);
    setDeleteMsg('');
    const failed: string[] = [];
    for (const id of selectedIds) {
      try {
        await deleteFarmViaDigisakaApi(id);
      } catch {
        failed.push(id);
      }
    }
    const removed = new Set(selectedIds.filter((id) => !failed.includes(id)));
    setReport((prev) => {
      if (!prev) return prev;
      const nextIncorrect = (prev.incorrect || []).filter(
        (r) => r.farm_id == null || !removed.has(String(r.farm_id))
      );
      const okCount = prev.totals?.ok ?? (prev.ok || []).length;
      return {
        ...prev,
        incorrect: nextIncorrect,
        totals: {
          farms: okCount + nextIncorrect.length,
          ok: okCount,
          incorrect: nextIncorrect.length,
        },
        message: `Removed ${removed.size} farm(s). ${nextIncorrect.length} incorrect remaining.`,
      };
    });
    setSelected((prev) => {
      const next = { ...prev };
      for (const id of removed) delete next[id];
      return next;
    });
    setDeleteMsg(
      failed.length
        ? `Deleted ${selectedIds.length - failed.length}; failed: ${failed.join(', ')}`
        : `Deleted ${removed.size} farm(s).`
    );
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-start gap-2">
          <div>
            <h2 className="text-lg font-semibold">Check farms — location</h2>
            <p className="text-xs text-gray-600 mt-0.5 break-all">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Compares each farm centroid to Philippine admin boundaries (GEE) against this account’s
            monitoring area. Large catalogs (500+) may take several minutes. Location updates are
            not available here — delete incorrect farms or correct them upstream in digisaka.
          </p>

          {(needsToken || err) && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 space-y-2">
              <p className="text-xs text-sky-950 font-medium">
                Digisaka.app WebGIS access token
              </p>
              <p className="text-[11px] text-sky-900/80">
                Paste the portal token for this LGU from digisaka.app (WebGIS access token). It is
                used to load farms / monitoring area and is vaulted for this user.
              </p>
              <textarea
                value={token}
                onChange={(e) => setToken(e.target.value)}
                rows={2}
                spellCheck={false}
                placeholder="e.g. 2550|…"
                className="w-full border border-sky-200 rounded-md px-2 py-1.5 text-xs font-mono bg-white"
              />
              <button
                type="button"
                disabled={loading || !token.trim()}
                onClick={() => void runCheck(token)}
                className="text-xs font-medium px-2.5 py-1.5 rounded bg-sky-700 text-white disabled:opacity-40"
              >
                {loading ? 'Running…' : 'Run check with this token'}
              </button>
            </div>
          )}

          {loading && (
            <p className="text-sm text-gray-700">
              Running GEE location check (batched reduceRegions)…
            </p>
          )}
          {err && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded px-3 py-2">
              {err}
            </p>
          )}
          {report && !loading && (
            <>
              <div className="text-sm text-gray-800 space-y-1">
                <p>
                  <span className="text-gray-500">Monitoring area:</span>{' '}
                  {monitoringAreaLabel(report.monitoring_area as Record<string, unknown> | null)}
                </p>
                <p>
                  <span className="font-medium text-emerald-800">{report.totals?.ok ?? 0} OK</span>
                  {' · '}
                  <span className="font-medium text-red-800">
                    {report.totals?.incorrect ?? incorrect.length} incorrect
                  </span>
                  {' · '}
                  <span className="text-gray-600">{report.totals?.farms ?? 0} total</span>
                </p>
                {report.message ? (
                  <p className="text-xs text-gray-500">{report.message}</p>
                ) : null}
                {report.error ? (
                  <p className="text-xs text-red-600">{report.error}</p>
                ) : null}
              </div>
              {incorrect.length === 0 ? (
                <p className="text-sm text-emerald-800">No incorrectly located farms found.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="text-xs text-sky-700 hover:underline"
                      onClick={() => toggleAll(true)}
                    >
                      Select all incorrect
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-600 hover:underline"
                      onClick={() => toggleAll(false)}
                    >
                      Clear selection
                    </button>
                    <button
                      type="button"
                      disabled={deleting || selectedIds.length === 0}
                      onClick={() => void deleteSelected()}
                      className="ml-auto text-xs font-medium px-2.5 py-1 rounded bg-red-700 text-white disabled:opacity-40"
                    >
                      {deleting ? 'Deleting…' : `Delete selected (${selectedIds.length})`}
                    </button>
                  </div>
                  {deleteMsg ? (
                    <p className="text-xs text-gray-700">{deleteMsg}</p>
                  ) : null}
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-left text-gray-600">
                        <tr>
                          <th className="p-2 w-8" />
                          <th className="p-2">Farm ID</th>
                          <th className="p-2">Name</th>
                          <th className="p-2">Declared</th>
                          <th className="p-2">GEE P-codes (prov/city/brgy)</th>
                          <th className="p-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incorrect.map((row) => {
                          const id = String(row.farm_id ?? '');
                          return (
                            <tr key={id || row.name || Math.random()} className="border-t border-gray-100">
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  checked={!!selected[id]}
                                  onChange={(e) =>
                                    setSelected((s) => ({ ...s, [id]: e.target.checked }))
                                  }
                                />
                              </td>
                              <td className="p-2 font-mono">{id}</td>
                              <td className="p-2">{row.name || '—'}</td>
                              <td className="p-2 text-gray-700">
                                {[
                                  row.declared_province_name || row.declared_province_id,
                                  row.declared_city_name || row.declared_city_id,
                                  row.declared_barangay_name || row.declared_barangay_id,
                                ]
                                  .filter(Boolean)
                                  .join(' / ') || '—'}
                              </td>
                              <td className="p-2 text-gray-700">
                                {row.gee
                                  ? [row.gee.province, row.gee.municipality, row.gee.barangay]
                                      .filter(Boolean)
                                      .join(' / ') || '—'
                                  : '—'}
                              </td>
                              <td className="p-2 text-red-800">{row.reason || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="p-3 border-t flex justify-end gap-2">
          {!loading && (
            <button
              type="button"
              onClick={() => void runCheck(token.trim() || undefined)}
              className="text-sm px-3 py-1.5 rounded border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            >
              Re-run check
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
